import { Link, useParams } from 'react-router-dom';
import { ONBOARDING_STAGES, OnboardingStage } from '@/types/onboarding';
import { cn } from '@/lib/utils';
import { CheckCircle2 } from 'lucide-react';

interface StageBreadcrumbNavigationProps {
  currentStage: OnboardingStage | string;
  projectId?: string;
  workspaceId?: string;
}

export default function StageBreadcrumbNavigation({
  currentStage,
  projectId,
  workspaceId,
}: StageBreadcrumbNavigationProps) {
  // Map database stage names to stage numbers
  const stageNameToNumber: Record<string, number> = {
    'initiated': 1,
    'data_import': 2,
    'validation': 3,
    'mapping': 4,
    'configuration': 5,
    'review': 6,
    'go_live': 7,
    'completed': 7,
  };

  const getCurrentStageNumber = () => {
    const stageNumber = stageNameToNumber[currentStage.toLowerCase()];
    if (stageNumber) return stageNumber;
    
    // Try to extract from path
    const pathMatch = currentStage.match(/stage-(\d+)/);
    if (pathMatch) return parseInt(pathMatch[1]);
    
    return 1;
  };

  const currentStageNum = getCurrentStageNumber();

  const getStageStatus = (stageNumber: number) => {
    if (stageNumber < currentStageNum) return 'completed';
    if (stageNumber === currentStageNum) return 'current';
    return 'upcoming';
  };

  const getStagePath = (stageNumber: number) => {
    if (!projectId || !workspaceId) return undefined;
    const stage = ONBOARDING_STAGES.find(s => s.number === stageNumber);
    return stage ? `/w/${workspaceId}/dashboard/tum/onboarding/project/${projectId}/${stage.path}` : undefined;
  };

  return (
    <nav className="flex items-center space-x-0 overflow-x-auto" aria-label="Stage Breadcrumb">
      {ONBOARDING_STAGES.map((stage, index) => {
        const status = getStageStatus(stage.number);
        const isLast = index === ONBOARDING_STAGES.length - 1;
        const stagePath = getStagePath(stage.number);
        const isClickable = status === 'completed' || status === 'current';

        return (
          <div key={stage.number} className="flex items-center">
            {/* Breadcrumb Segment */}
            <div className="relative flex items-center">
              {/* Main Segment */}
              <div
                className={cn(
                  "relative px-4 py-2.5 text-sm font-medium transition-all z-10",
                  status === 'completed' && "bg-primary text-primary-foreground",
                  status === 'current' && "bg-pink-500 text-white ring-2 ring-pink-500/30",
                  status === 'upcoming' && "bg-muted text-muted-foreground",
                  isClickable && stagePath && status === 'current' && "hover:bg-pink-600 cursor-pointer",
                  isClickable && stagePath && status === 'completed' && "hover:bg-primary/80 cursor-pointer",
                  !isClickable && "opacity-60",
                  index === 0 && "rounded-l-md",
                  isLast && "rounded-r-md"
                )}
                style={{
                  paddingLeft: index === 0 ? '1rem' : '1.75rem',
                  paddingRight: isLast ? '1rem' : '1.75rem',
                }}
              >
                {isClickable && stagePath ? (
                  <Link
                    to={stagePath}
                    className="flex items-center gap-2 whitespace-nowrap"
                    onClick={(e) => {
                      if (!isClickable) e.preventDefault();
                    }}
                  >
                    {status === 'completed' && (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    <span>Stage {stage.number}</span>
                    <span className="hidden sm:inline">: {stage.name.split(' ')[0]}</span>
                  </Link>
                ) : (
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    {status === 'completed' && (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    <span>Stage {stage.number}</span>
                    <span className="hidden sm:inline">: {stage.name.split(' ')[0]}</span>
                  </div>
                )}
              </div>
              
              {/* Arrow connector (not for last item) */}
              {!isLast && (
                <div
                  className={cn(
                    "absolute right-0 top-0 w-0 h-0 z-20",
                    status === 'completed' && "border-t-[20px] border-b-[20px] border-l-[12px] border-l-primary border-t-transparent border-b-transparent",
                    status === 'current' && "border-t-[20px] border-b-[20px] border-l-[12px] border-l-pink-500 border-t-transparent border-b-transparent",
                    status === 'upcoming' && "border-t-[20px] border-b-[20px] border-l-[12px] border-l-muted border-t-transparent border-b-transparent"
                  )}
                  style={{
                    right: '-12px',
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

